import { useRef, useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import { wacToken } from "@/libraries/design/foundations";
import { DataEventProps } from "@/libraries/wpl/foundations/src/index";

import { ImageProps } from "../../image/src/image";

export type EmbeddedVideoType = "youtube" | "mp4" | "loom";

const PlayIcon = () => {
    return (
        <svg
            className="play-icon"
            css={css({
                fill: wacToken("color.neutral.light0"),
                opacity: ".9",
                position: "absolute",
                insetInlineStart: "0",
                insetInlineEnd: "0",
                insetBlockStart: "0",
                insetBlockEnd: "0",
                margin: "auto",
                pointerEvents: "none"
            })}
            width="70px"
            height="70px"
            viewBox="0 0 32 32"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
        >
            <path d="M16,0 C7.163444,-5.41083001e-16 1.082166e-15,7.163444 0,16 C-1.082166e-15,24.836556 7.163444,32 16,32 C24.836556,32 32,24.836556 32,16 C32,11.7565362 30.3142906,7.68687356 27.3137085,4.6862915 C24.3131264,1.68570945 20.2434638,2.5983722e-16 16,0 Z M11.1288889,22.2222222 L11.1288889,9.77777778 L23.6533333,16 L11.1288889,22.2222222 Z" />
        </svg>
    );
};

/**
 * Detects the video type from the URL.
 * - URLs containing "loom.com" are treated as Loom videos
 * - URLs ending in ".mp4" or containing "video/mp4" are treated as MP4 videos
 * - Everything else is treated as a YouTube embed (default, backward-compatible)
 */
export const detectVideoType = (url: string): EmbeddedVideoType => {
    const normalizedUrl = url.toLowerCase();
    if (normalizedUrl.includes("loom.com")) {
        return "loom";
    }
    if (normalizedUrl.endsWith(".mp4") || normalizedUrl.includes("video/mp4")) {
        return "mp4";
    }
    return "youtube";
};

/**
 * Normalizes a Loom URL to its embed format.
 * Converts share URLs (loom.com/share/ID) to embed URLs (loom.com/embed/ID).
 */
const normalizeLoomUrl = (url: string): string => {
    return url.replace("/share/", "/embed/");
};

/**
 * Normalizes a YouTube URL to its embed format.
 */
const normalizeYoutubeUrl = (url: string): string => {
    return url
        .replace("youtu.be/", "youtube.com/embed/")
        .replace("https://youtube.com", "https://www.youtube.com");
};

export interface EmbeddedVideoProps {
    embedUrl: string;
    title?: string;
    thumbnail?: ImageProps;
    dataEventProps?: DataEventProps;
    videoType?: EmbeddedVideoType;
}

export const EmbeddedVideo = ({
    embedUrl,
    title,
    thumbnail,
    dataEventProps,
    videoType
}: EmbeddedVideoProps) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const resolvedVideoType = videoType ?? detectVideoType(embedUrl);

    const videoOverlayCss = css({
        minHeight: "100%",
        width: "100%",
        position: "relative",
        border: "0",
        cursor: "pointer",
        overflow: "hidden",
        display: "flex"
    });

    const iFrameCss = css({
        verticalAlign: "bottom",
        display: "block",
        aspectRatio: "16/9",
        width: "100%",
        height: "auto",
        allow: "autoplay",
        border: "none"
    });

    const mp4VideoCss = css({
        display: "block",
        aspectRatio: "16/9",
        width: "100%",
        height: "auto",
        backgroundColor: wacToken("color.primary.black")
    });

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setIsPlaying(true);
        createAnalyticsEvent({
            eventType: "ui",
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: "embeddedVideoPlay",
            source: dataEventProps?.["data-event-container"] ?? "embeddedVideo",
            attributes: {
                ...(dataEventProps?.["data-product-key"] && {
                    productKey: dataEventProps["data-product-key"]
                }),
                ...(dataEventProps?.["data-label"] && {
                    label: dataEventProps["data-label"]
                }),
                videoType: resolvedVideoType
            }
        }).fire();
    };

    // needs to be defined outside of css
    const bgImage = `url("${thumbnail?.src}")`;

    const intl = useIntl();

    const renderPlayingContent = () => {
        switch (resolvedVideoType) {
            case "mp4":
                return (
                    <video
                        ref={videoRef}
                        css={mp4VideoCss}
                        src={embedUrl}
                        title={title}
                        controls
                        autoPlay
                        playsInline
                        aria-label={title ?? ""}
                    >
                        <track kind="captions" />
                        {intl.formatMessage({
                            id: "embeddedVideo.videoNotSupported",
                            defaultMessage: "Video not supported."
                        })}
                    </video>
                );
            case "loom": {
                const loomUrl = normalizeLoomUrl(embedUrl);
                return (
                    <iframe
                        css={iFrameCss}
                        width="100%"
                        height="auto"
                        src={loomUrl}
                        title={title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                        allowFullScreen={true}
                    />
                );
            }
            case "youtube":
            default: {
                const youtubeUrl = normalizeYoutubeUrl(embedUrl);
                if (youtubeUrl.includes("iframe")) {
                    return (
                        <div
                            css={css({
                                // NOTE: Nested selectors are necessary here to be able to style the dangerouslySetInnerHTML blob
                                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                                iframe: {
                                    verticalAlign: "bottom",
                                    display: "block",
                                    aspectRatio: "16/9",
                                    width: "100%",
                                    height: "auto",
                                    allow: "autoplay"
                                }
                            })}
                            dangerouslySetInnerHTML={{
                                __html: youtubeUrl
                            }}
                        />
                    );
                }
                return (
                    <iframe
                        css={iFrameCss}
                        width="100%"
                        height="auto"
                        src={`${youtubeUrl}${
                            youtubeUrl.includes("?") ? "&" : "?"
                        }autoplay=1`}
                        title={title}
                        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; autoplay;"
                        allowFullScreen={true}
                        data-gtm-yt-inspected-12="true"
                    />
                );
            }
        }
    };

    return (
        <div css={css({ overflow: "hidden" })}>
            {!isPlaying && (
                <div css={videoOverlayCss}>
                    <button
                        type="button"
                        className="play-trigger"
                        css={css({
                            display: "block",
                            width: "100%",
                            minHeight: "100%",
                            padding: 0,
                            border: "none"
                        })}
                        aria-label={intl.formatMessage({
                            id: "embeddedVideo.playTriggerButton.ariaLabel",
                            defaultMessage: "Play Video"
                        })}
                        onClick={handleClick}
                    >
                        {/* eslint-disable-next-line no-restricted-syntax */}
                        <img
                            css={css({
                                "maxWidth": "100%",
                                "width": "100%",
                                "height": "auto",
                                "transformOrigin": "center center",
                                "transform": "scale(1)",
                                "transition": "all 1.3s",
                                "backgroundImage": bgImage,
                                "backgroundSize": "cover",
                                "backgroundPosition": "center center",
                                "backgroundColor": "rgba(23, 43, 77, .15)",
                                "backgroundBlendMode": "multiply",
                                "verticalAlign": "bottom",
                                "pointerEvents": "none",
                                ":hover": {
                                    backgroundColor: wacToken(
                                        "color.neutral.light0"
                                    ),
                                    transform: "scale(1.06)"
                                }
                            })}
                            src={
                                "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAJCAYAAAA7KqwyAAAAAXNSR0IArs4c6QAAAAlwSFlzAAALEwAACxMBAJqcGAAAAVlpVFh0WE1MOmNvbS5hZG9iZS54bXAAAAAAADx4OnhtcG1ldGEgeG1sbnM6eD0iYWRvYmU6bnM6bWV0YS8iIHg6eG1wdGs9IlhNUCBDb3JlIDUuNC4wIj4KICAgPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4KICAgICAgPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9IiIKICAgICAgICAgICAgeG1sbnM6dGlmZj0iaHR0cDovL25zLmFkb2JlLmNvbS90aWZmLzEuMC8iPgogICAgICAgICA8dGlmZjpPcmllbnRhdGlvbj4xPC90aWZmOk9yaWVudGF0aW9uPgogICAgICA8L3JkZjpEZXNjcmlwdGlvbj4KICAgPC9yZGY6UkRGPgo8L3g6eG1wbWV0YT4KTMInWQAAABFJREFUKBVjYBgFoyFAhRAAAAJJAAFEkgypAAAAAElFTkSuQmCC"
                            }
                            alt={thumbnail?.alt ?? ""}
                        />
                        <PlayIcon />
                    </button>
                </div>
            )}
            {isPlaying && renderPlayingContent()}
        </div>
    );
};
